import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ChangeEvent,
} from 'react';
import { Button, Flex, message } from 'antd';
import {
  AICallIcon,
  GreenTickCircleOutlineIcon,
  GxPAlertBannerIcon,
  MailIcon,
  PhoneIcon,
} from '../../../assets/icons/icons';
import { StatusCardNotification, AlertsNavButtons } from '../../../components';
import { WarningFilled } from '@ant-design/icons';
import {
  ResolveAlertComments,
  ResolveAlertCommentsModel,
  AcknowledgeShipmentAlertActivity,
  terminateAiEmail,
} from '../../../apis/shipment';
import ShipmentAlertCommentBox from './ShipmentAlertCommentBox';
import {
  FeaturePermissionMatrix,
  AlertCloseTagOptions,
  AlertOwnershipSnapshot,
  primaryContact,
  SingleShipmentDataExtended,
} from '../../../apis/types';
import { BeaconEventType } from '@/common/enum';
import { useToken } from '@/common/helper';
import MarkAsDeliveredModal from '../ControlTower/MarkAsDeliveredModal';
import { useNavigate } from 'react-router-dom';
import { useGateValue } from '@statsig/react-bindings';
import { useSpin } from '@/components/SpinContext';
import { DismissGxPAlert } from '@/apis/gxpReport';
import { trackEvent } from '@/hooks';
import AlertAssignmentPanel, {
  type CloseAlertValues,
} from './AlertAssignmentPanel';
import './ShipmentAlerts.css';

const AlertAssignmentAtIcon = () => (
  <svg
    aria-hidden
    className='alert-assignment-at shipment-alerts__no-shrink'
    // the same size as the Use AI icons, the card's other button icons
    width='12'
    height='12'
    viewBox='9 9 10 10'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M14 9C11.24 9 9 11.24 9 14C9 16.76 11.24 19 14 19H16.5V18H14C11.83 18 10 16.17 10 14C10 11.83 11.83 10 14 10C16.17 10 18 11.83 18 14V14.715C18 15.11 17.645 15.5 17.25 15.5C16.855 15.5 16.5 15.11 16.5 14.715V14C16.5 12.62 15.38 11.5 14 11.5C12.62 11.5 11.5 12.62 11.5 14C11.5 15.38 12.62 16.5 14 16.5C14.69 16.5 15.32 16.22 15.77 15.765C16.095 16.21 16.655 16.5 17.25 16.5C18.235 16.5 19 15.7 19 14.715V14C19 11.24 16.76 9 14 9ZM14 15.5C13.17 15.5 12.5 14.83 12.5 14C12.5 13.17 13.17 12.5 14 12.5C14.83 12.5 15.5 13.17 15.5 14C15.5 14.83 14.83 15.5 14 15.5Z'
      fill='currentColor'
    />
  </svg>
);

// TODO(tim): de-dupe this (it is probably ShipmentAlertData, from types.ts)
interface Alert {
  id?: string;
  title?: string;
  detail?: string;
  alert_type?: string;
  is_transport_provider_exception?: boolean;
  ai_summary?: string;
  recommended_action?: string;
  is_critical?: boolean;
  can_use_ai?: boolean;
  ai_status?: string;
  created_at?: string;
  ai_start_time?: string | null;
  permissions?: FeaturePermissionMatrix;
  contact_type?: contact_type;
  primary_contact?: primaryContact;
  report_id?: string;
  is_published?: boolean;
  status?: string;
  ownership?: AlertOwnershipSnapshot;
  close_tag_options?: AlertCloseTagOptions;
}

interface ShipmentAlertsProps {
  checkTokenExpiry: () => string;
  alertID: string | null;
  shipmentSuccess: () => void;
  setStartAlertsInterval: (value: boolean) => void;
  alertsArray?: Alert[];
  OnUseAICallClick: (alert: Alert) => void;
  OnUseAIEmailClick: (alert: Alert) => void;
  shipment?: SingleShipmentDataExtended;
  setAlertID: (id: string | null) => void;
  onAlertSucccess: () => void;
  shipmentId: string;
  unauthorize: (error: unknown) => void;
  handleMarkAsDevliered: () => void;
  toggleDrawer?: (
    action: string | boolean,
    type?: string,
    id?: string,
    alert?: unknown
  ) => void;
}
export interface contact_type {
  routing_key?: string;
  recommended_contact?: string;
}

export const getAlertColors = (
  alert: Alert,
  isBoundCall: boolean,
  isGxp: boolean
) => {
  if (isBoundCall) {
    return {
      bgColor: '#1948A3CF',
      color: '#fff',
      actionButtonBgColor: '#fff',
      actionButtonTextColor: '#000',
      dismissButtonTextColor: '#000',
    };
  }

  if (isGxp) {
    return {
      bgColor: 'var(--light-gray)',
      color: 'var(--text-primary)',
      actionButtonBgColor: '#000',
      actionButtonTextColor: '#fff',
      dismissButtonTextColor: 'var(--text-primary)',
    };
  }

  if (alert?.is_critical) {
    return {
      // Fixed red fill, not `var(--danger)`: that token intentionally lightens
      // to a pastel salmon in dark mode (it's tuned for text-on-dark use), which
      // leaves this card's white text/buttons low-contrast. '#DE1135' is that
      // token's own light-mode value, so light mode is pixel-identical and dark
      // mode keeps the same deep red white reads clearly on.
      bgColor: '#DE1135',
      color: '#fff',
      actionButtonBgColor: '#BB032A',
      actionButtonTextColor: '#fff',
      dismissButtonTextColor: '#fff',
    };
  }

  return {
    bgColor: '#F6BC2F',
    // Fixed yellow fill, not a themed surface: stays black-on-yellow in both
    // themes. `rgb(0, 0, 0)` (not '#000'/'black') deliberately avoids
    // CustomParagraph/CustomTitle's legacy-black-text auto remap, which would
    // otherwise flip this to near-white and make it unreadable on the yellow.
    color: 'rgb(0, 0, 0)',
    actionButtonBgColor: '#FBE5B6',
    actionButtonTextColor: '#000',
    dismissButtonTextColor: '#000',
  };
};

export const getAlertIcon = (
  alert: Alert,
  isBoundCall: boolean,
  isGxp: boolean
) => {
  if (isBoundCall) {
    return <AICallIcon color='white' />;
  }

  if (!isGxp) {
    return <WarningFilled style={{ fontSize: 16, flexShrink: 0 }} />;
  }

  if (alert?.is_published) {
    return (
      <GreenTickCircleOutlineIcon
        color='var(--green-900)'
        width={24}
        height={24}
      />
    );
  }

  return (
    <GxPAlertBannerIcon color={alert?.is_critical ? '#DE1135' : '#9F6402'} />
  );
};

/**
 * ShipmentAlerts component displays and manages alerts for a shipment
 *
 * @param checkTokenExpiry - Function to get valid authentication token
 * @param alertID - Currently selected alert ID
 * @param shipmentSuccess - Callback when shipment operation succeeds
 * @param setStartAlertsInterval - Controls alert polling interval
 * @param alertsArray - Array of alerts to display
 * @param OnUseAICallClick - Handler for AI call action
 * @param OnUseAIEmailClick - Handler for AI email action
 * @param shipment - Shipment data containing contact information
 * @param setAlertID - Sets the currently selected alert ID
 * @param onAlertSucccess - Callback when alert operation succeeds
 * @param shipmentId - Unique shipment identifier
 * @param unauthorize - Handler for authorization errors
 */

const ShipmentAlerts: React.FC<ShipmentAlertsProps> = ({
  checkTokenExpiry,
  alertID,
  shipmentSuccess,
  setStartAlertsInterval,
  alertsArray: alertsArrayProp = [],
  OnUseAICallClick,
  OnUseAIEmailClick,
  shipment,
  setAlertID,
  onAlertSucccess,
  shipmentId,
  unauthorize,
  // handleMarkAsDevliered,
  toggleDrawer,
}) => {
  const { decodedUserToken, token } = useToken();
  const decodedToken = decodedUserToken();
  const navigate = useNavigate();
  const { showSpin, hideSpin } = useSpin();
  const [showDeliveredModal, setShowDeliveredModal] = useState<boolean>(false);
  // when user enables ai for alerts and master AI from AI control screen.
  const aiAlertPermissions =
    decodedToken?.ai_enable && decodedToken?.alerts_enabled;
  const [alertIndex, setAlertIndex] = useState(0);
  const [openAlertInput, setOpenAlertInput] = useState(false);
  const [isResolved, setIsResolved] = useState(true);
  const [alertComment, setAlertComment] = useState('');
  const [assignmentAlertId, setAssignmentAlertId] = useState<string | null>(
    null
  );
  // Tagged Mark as resolved / Dismiss panel (alert_assignment gate on). With
  // the gate off, the original comment box below is used unchanged.
  const taggedCloseEnabled = useGateValue('alert_assignment');
  const [closeAction, setCloseAction] = useState<{
    alertId: string;
    isResolved: boolean;
  } | null>(null);
  // Newest alerts first so the sidepanel carousel opens on the most recent issue.
  const alertsArray = useMemo(() => {
    return [...alertsArrayProp].sort((a, b) => {
      const aTime = Date.parse(a?.created_at ?? '') || 0;
      const bTime = Date.parse(b?.created_at ?? '') || 0;
      return bTime - aTime;
    });
  }, [alertsArrayProp]);

  const eligibleAlerts = [
    'delivery_overdue',
    'driver_cant_find_location',
    'delivered_to_wrong_address',
    'delivered_to_wrong_recipient',
    'proof_of_delivery_missing',
    'transport_provider_exception',
    //  new alert type for transport provider exceptions
    'customer_not_available_or_business_closed',
    'incorrect_address',
    'rescheduled_by_recipient',
    'rescheduled_by_transport_provider',
    'delivery_restrictions',
    'refused_by_recipient',
    'weather_impact',
    'customs_issue',
    'cancelled_by_sender',
    'other',
    'operational_delay',
  ];

  const isAlertEligibleForMarkAsDelivered = (
    alertId: string,
    alertsArray: Alert[] = [],
    isResolvedAction = true
  ) => {
    const clickedAlert = alertsArray.find((a) => a?.id === alertId);
    if (!clickedAlert) return false;

    const alertType = clickedAlert?.alert_type || '';
    const isCantFind = alertType === 'driver_cant_find_location';

    const normalizedStatus = (shipment?.status ?? '')
      .toUpperCase()
      .replace(/[\s\-_]+/g, '_');

    const statusOk =
      normalizedStatus === 'DELIVERY_EXCEPTION' ||
      (isCantFind && normalizedStatus === 'IN_TRANSIT');
    if (!statusOk) return false;

    if (isCantFind && !isResolvedAction) return false;

    const openEligibleAlerts = alertsArray.filter(
      (a) => a?.alert_type && eligibleAlerts.some((t) => t === a?.alert_type)
    );

    return (
      eligibleAlerts.some((t) => t === alertType) &&
      openEligibleAlerts.length === 1
    );
  };

  // Alerts function when comment cancel clicks or submit is successful
  const handleToggleAlertInput = useCallback(() => {
    setCloseAction(null);
    setOpenAlertInput(false);
    setAlertComment('');
    setAlertID(null);
    setStartAlertsInterval(true);
  }, [setAlertID, setStartAlertsInterval]);

  useEffect(() => {
    handleToggleAlertInput();
    setAlertIndex(0);
  }, [handleToggleAlertInput, shipmentId]);

  // Close draft comment when navigating between alerts (Cancel semantics)
  const isFirstAlertIndexEffect = useRef(true);
  useEffect(() => {
    if (isFirstAlertIndexEffect.current) {
      isFirstAlertIndexEffect.current = false;
      return;
    }
    handleToggleAlertInput();
  }, [alertIndex, handleToggleAlertInput]);

  // Track the active alert becoming visible (drawer opened on it, or
  // navigated to via the nav arrows). Keyed on the alert's own id rather
  // than the array reference, so a background poll refreshing alertsArray
  // doesn't re-log the same alert that's already on screen.
  const activeAlertId = alertsArray[alertIndex]?.id;
  useEffect(() => {
    if (!activeAlertId || !shipmentId) return;
    trackEvent(BeaconEventType.AlertViewed, {
      reference_id: activeAlertId,
      shipment_id: shipmentId,
    });
  }, [activeAlertId, shipmentId]);

  // Opening an alert's Assign / Resolve / Dismiss panel is an explicit human
  // view, so acknowledge the shipment's alert activity once per opened panel.
  // A failure here must never get in the user's way.
  const openPanelAlertId = closeAction?.alertId ?? assignmentAlertId;
  useEffect(() => {
    if (!taggedCloseEnabled || !openPanelAlertId || !shipmentId) return;
    AcknowledgeShipmentAlertActivity(token ?? '', shipmentId).catch(
      (): void => undefined
    );
  }, [taggedCloseEnabled, openPanelAlertId, shipmentId, token]);

  // When comments input for alert enter
  const onAlertTextareaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setAlertComment(e.target.value);
  };

  // Open optional comment box for resolve (true) or dismiss (false)
  const handleOpenAlertCommentBox = (
    val: boolean,
    id: string,
    is_gxp: boolean = false
  ) => {
    if (is_gxp) {
      const _ = navigate(`/publish-gxp-report/${id}`);
    } else if (taggedCloseEnabled) {
      // Only one panel per alert: opening this discards an open Assign panel.
      setAssignmentAlertId(null);
      setCloseAction({ alertId: id, isResolved: val });
      setStartAlertsInterval(false);
    } else {
      setIsResolved(val);
      setAlertID(id);
      setOpenAlertInput(true);
      setStartAlertsInterval(false);
    }
  };

  // Runs after an alert was resolved or dismissed, by either panel.
  const handleAlertClosed = (alertId: string, isResolvedAction: boolean) => {
    const iseligible = isAlertEligibleForMarkAsDelivered(
      alertId,
      alertsArray,
      isResolvedAction
    );
    if (iseligible) {
      setShowDeliveredModal(true);
    }
    handleToggleAlertInput();
    const alertsInd = alertIndex;
    if (alertsArray.length > 0) {
      if (alertsInd < alertsArray.length - 1) {
        setAlertIndex(alertsInd + 1);
      } else {
        setAlertIndex(0);
      }
    } else {
      setAlertIndex(0);
    }
    onAlertSucccess();
    shipmentSuccess();
  };

  // Commit from the tagged panel. Rejects so the panel can show the error.
  const submitTaggedClose = async (
    alertId: string,
    isResolvedAction: boolean,
    { tagId, comment }: CloseAlertValues
  ): Promise<void> => {
    const res = await ResolveAlertComments(checkTokenExpiry(), {
      is_resolved: isResolvedAction,
      pk_list: [alertId],
      comment,
      tag_id: tagId,
      shipment_id: shipmentId,
    });
    if (!res?.data?.data?.is_success) throw res;
    handleAlertClosed(alertId, isResolvedAction);
  };

  const closeTaggedPanel = () => {
    setCloseAction(null);
    setStartAlertsInterval(true);
  };

  const ResolveAlert = (model: ResolveAlertCommentsModel) => {
    const token = checkTokenExpiry();
    const payload: ResolveAlertCommentsModel = {
      ...model,
      shipment_id: shipmentId,
    };

    ResolveAlertComments(token, payload)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            handleAlertClosed(model.pk_list[0], model.is_resolved);
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      });
  };
  // --------------------- Cancel AI Email ---------------------
  const handleCancelAiEmail = async (alertId?: string) => {
    if (!alertId) {
      message.error('Alert ID is required to cancel AI email');
      return;
    }

    try {
      const token = checkTokenExpiry();
      const payload = {
        alert_id: alertId,
        reason: 'User requested cancellation',
      };

      const response = await terminateAiEmail(token, payload);

      if (response?.data?.data?.is_success !== false) {
        message.success('AI email conversation has been canceled.');
        // Refresh alerts to show updated status
        onAlertSucccess();
        shipmentSuccess();
      } else {
        message.error(
          response?.data?.data?.message || 'Failed to cancel AI email'
        );
      }
    } catch (error: unknown) {
      console.error('Error canceling AI email:', error);
      const errorMessage = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data?.message;
      message.error(
        errorMessage || 'Failed to cancel AI email. Please try again.'
      );
      unauthorize(error);
    }
  };

  // --------------------- Handle Timer Expiration ---------------------
  const handleTimerExpire = async (alertId?: string) => {
    if (!alertId) {
      // Silent fail - backend task will handle it
      return;
    }

    try {
      const token = checkTokenExpiry();
      const payload = {
        alert_id: alertId,
        reason: 'AI email resolution window expired',
      };

      const response = await terminateAiEmail(token, payload);

      if (response?.data?.data?.is_success !== false) {
        // Refresh alerts to show updated status
        onAlertSucccess();
        shipmentSuccess();
        // Don't show message - less intrusive than manual cancel
        // Backend will have already updated the status
      } else {
        // Silent fail - backend task will handle timeout
        console.warn('[handleTimerExpire] Failed to terminate:', {
          message: response?.data?.data?.message,
        });
      }
    } catch (error: unknown) {
      // Silent fail - backend task will handle timeout
      console.error('[handleTimerExpire] Error:', error);
      // Don't show error to user - backend task will handle it
    }
  };

  const handleDismissGxPAlert = useCallback(
    (reportID?: string) => {
      showSpin();
      const model = {
        enrich: true,
      };
      DismissGxPAlert(token ?? '', reportID, model)
        .then((res) => {
          const data = res?.data?.data?.data;
          if (data) {
            message.success(res?.data?.data?.message || 'GxP Alert dismissed.');
            onAlertSucccess();
            shipmentSuccess();
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    },
    [token, showSpin, hideSpin, unauthorize, onAlertSucccess, shipmentSuccess]
  );

  const onSubmitAlertComment = () => {
    const payload: ResolveAlertCommentsModel = {
      is_resolved: isResolved,
      pk_list: [alertID!],
      comment: alertComment,
    };

    ResolveAlert(payload);
  };

  return (
    <>
      {alertsArray.length > 0 &&
        alertsArray.map((alert: Alert, index: number) => {
          const isBoundCall =
            alert?.alert_type === 'in_bound' ||
            alert?.alert_type === 'out_bound';
          const is_gxp = alert?.alert_type === 'temperature_gxp';
          const colors = getAlertColors(alert, isBoundCall, is_gxp);
          const canManageAlert =
            alert?.permissions?.is_create === true ||
            alert?.permissions?.is_update === true;
          const ownership = alert.ownership;
          const canViewAssignment =
            ownership?.enabled === true &&
            ownership?.permissions?.is_view === true;
          const canEditAssignment =
            canViewAssignment && ownership?.permissions?.is_update === true;
          const assignmentSelections = ownership?.assignment?.selections ?? [];
          const assignmentOwners = ownership?.assignment?.owners ?? [];
          const assignmentCount =
            assignmentOwners.length || assignmentSelections.length;
          const firstAssignmentLabel =
            assignmentOwners[0]?.name ?? assignmentSelections[0]?.label;
          const assignmentName = firstAssignmentLabel ?? 'Assign';
          const additionalAssignmentCount = Math.max(assignmentCount - 1, 0);
          return (
            <React.Fragment key={alert.id}>
              {alertIndex === index && (
                <>
                  <Flex
                    vertical
                    gap={8}
                    className='shipment-alerts__card'
                    style={{ marginBottom: '28px' }}
                  >
                    {alertsArray.length > 1 && (
                      <AlertsNavButtons
                        alertsArrayLength={alertsArray.length}
                        alertIndex={alertIndex}
                        setAlertIndex={setAlertIndex}
                        index={index}
                      />
                    )}
                    <StatusCardNotification
                      showButtons={canManageAlert}
                      showDismissButton={canManageAlert || is_gxp}
                      cardStyle={
                        canManageAlert || is_gxp || canViewAssignment
                          ? 'style-2'
                          : 'style-1'
                      }
                      leadingAction={
                        canViewAssignment && alert.id ? (
                          <Button
                            className='alert-card-btn-text'
                            aria-label={
                              additionalAssignmentCount > 0
                                ? `${assignmentName} +${additionalAssignmentCount}`
                                : assignmentName === 'Assign'
                                  ? 'Assign alert owner'
                                  : assignmentName
                            }
                            disabled={!canEditAssignment}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (canEditAssignment) {
                                setCloseAction(null);
                                setAssignmentAlertId((current) =>
                                  current === alert.id ? null : alert.id!
                                );
                              }
                            }}
                            style={{
                              height: 36,
                              width: 'fit-content',
                              maxWidth: 150,
                              flexShrink: 0,
                              borderRadius: 8,
                              background: colors.actionButtonBgColor,
                              color: colors.actionButtonTextColor,
                              border: 0,
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              paddingInline: 8,
                            }}
                          >
                            <span className='alert-assignment-button-content shipment-alerts__assignee-content'>
                              <AlertAssignmentAtIcon />
                              <span className='alert-assignment-label shipment-alerts__assignee-label'>
                                <span className='alert-assignment-name shipment-alerts__assignee-name'>
                                  {assignmentName}
                                </span>
                                {additionalAssignmentCount > 0 && (
                                  <span className='alert-assignment-count shipment-alerts__no-shrink'>
                                    +{additionalAssignmentCount}
                                  </span>
                                )}
                              </span>
                            </span>
                          </Button>
                        ) : undefined
                      }
                      expandedContent={
                        taggedCloseEnabled &&
                        alert.id &&
                        closeAction?.alertId === alert.id ? (
                          <AlertAssignmentPanel
                            mode={
                              closeAction.isResolved ? 'resolve' : 'dismiss'
                            }
                            alertId={alert.id}
                            shipmentId={shipmentId}
                            token={token ?? ''}
                            closeTags={
                              closeAction.isResolved
                                ? alert.close_tag_options?.resolution
                                : alert.close_tag_options?.dismissal
                            }
                            onSubmitClose={(values) =>
                              submitTaggedClose(
                                alert.id!,
                                closeAction.isResolved,
                                values
                              )
                            }
                            onCancel={closeTaggedPanel}
                            onCommitted={closeTaggedPanel}
                            onUnauthorized={unauthorize}
                          />
                        ) : assignmentAlertId === alert.id &&
                          alert.id &&
                          ownership &&
                          canEditAssignment ? (
                          <AlertAssignmentPanel
                            alertId={alert.id}
                            shipmentId={shipmentId}
                            token={token ?? ''}
                            ownership={ownership}
                            onCancel={() => setAssignmentAlertId(null)}
                            onCommitted={() => {
                              setAssignmentAlertId(null);
                              onAlertSucccess();
                              shipmentSuccess();
                            }}
                            onUnauthorized={unauthorize}
                          />
                        ) : undefined
                      }
                      // inbound change here
                      icon={getAlertIcon(alert, isBoundCall, is_gxp)}
                      title={alert?.title || ''}
                      description={alert?.detail || ''}
                      // ai controls change here
                      // when user enables ai for alerts and master AI from AI control screen.
                      useAiButton={alert?.can_use_ai && aiAlertPermissions}
                      aiStatus={alert?.ai_status}
                      createdAt={alert?.created_at || ''}
                      shipment={shipment}
                      alertType={alert?.alert_type}
                      is_TP_exception={alert?.is_transport_provider_exception}
                      AiSummary={alert?.ai_summary || ''}
                      recommendedAction={alert?.recommended_action}
                      contactType={alert?.contact_type}
                      primaryContact={alert?.primary_contact}
                      aiStartTime={alert?.ai_start_time}
                      bgColor={colors.bgColor}
                      color={colors.color}
                      actionButtonText={
                        is_gxp
                          ? alert?.status == 'awaiting_data'
                            ? 'Investigate'
                            : 'Review'
                          : 'Mark as Resolved'
                      }
                      showActionBtn={
                        is_gxp ? !alert?.is_published : canManageAlert
                      }
                      actionButtonBorderRadius='8px'
                      actionButtonBgColor={colors.actionButtonBgColor}
                      // inbound change here
                      actionButtonTextColor={colors?.actionButtonTextColor}
                      dismissButtonTextColor={colors?.dismissButtonTextColor}
                      dismissAction={() =>
                        is_gxp
                          ? handleDismissGxPAlert(alert?.report_id)
                          : handleOpenAlertCommentBox(false, alert?.id ?? '')
                      }
                      actionButtonOnClick={() =>
                        is_gxp
                          ? handleOpenAlertCommentBox(
                              false,
                              alert?.report_id ?? '',
                              true
                            )
                          : handleOpenAlertCommentBox(true, alert?.id ?? '')
                      }
                      isCritical={alert.is_critical}
                      onCardClick={() => {
                        // Open email transcript drawer only when AI email is in progress
                        if (
                          toggleDrawer &&
                          shipment?.can_view_ai_transcripts !== false &&
                          alert?.ai_status === 'ai_email_in_progress'
                        ) {
                          toggleDrawer(
                            'open',
                            'emailTranscripts',
                            shipmentId,
                            alert
                          );
                        }
                      }}
                      onCancelAiEmail={
                        alert?.ai_status === 'ai_email_in_progress'
                          ? () => handleCancelAiEmail(alert.id)
                          : undefined
                      }
                      onExpireAiEmail={
                        alert?.ai_status === 'ai_email_in_progress'
                          ? () => handleTimerExpire(alert.id)
                          : undefined
                      }
                      aiOptions={[
                        {
                          key: 'call',
                          label: 'Call phone',
                          icon: <PhoneIcon width={14} height={14} />,
                          onClick: () => {
                            OnUseAICallClick(alertsArray[alertIndex]);
                          },
                          disabled: !alert?.primary_contact?.phone,
                        },
                        {
                          key: 'email',
                          label: 'Send email',
                          icon: <MailIcon width={14} height={14} />,
                          onClick: () => {
                            OnUseAIEmailClick(alertsArray[alertIndex]);
                          },
                          disabled: !alert?.primary_contact?.email,
                        },
                      ]}
                    />
                  </Flex>
                </>
              )}
            </React.Fragment>
          );
        })}

      {openAlertInput && (
        <ShipmentAlertCommentBox
          onAlertTextareaChange={(val) => onAlertTextareaChange(val)}
          handleToggleAlertInput={handleToggleAlertInput}
          onSubmitAlertComment={onSubmitAlertComment}
        />
      )}

      {showDeliveredModal && shipment && (
        <MarkAsDeliveredModal
          shipmentData={shipment}
          open={showDeliveredModal}
          type={'single'}
          onCancel={() => setShowDeliveredModal(false)}
          onSuccess={shipmentSuccess}
        />
      )}
    </>
  );
};

export default ShipmentAlerts;
